import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable } from 'rxjs';
import { SchemaService } from 'src/app/shared/symptoms/services/schema/schema.service';
import { ChipColour } from '../../../../chip/chip.component';
import { DryEyeFormGroup } from '../../dry-eye-form/dry-eye-form.model';
import { DewsIIIService, HomeostasisCategory, HomeostasisResult } from '../dews-iii.service';
import { HomeostasisCategoryKey, HOMEOSTASIS_SIGN_KEYS } from '../dews-iii-sign-model';

export { HomeostasisCategoryKey } from '../dews-iii-sign-model';

export interface HomeostasisCategoryMeta {
  code: string;
  title: string;
  thresholds: string;
  testKeysRef: 'tearFilmStabilityKeys' | 'osmolarityKeys' | 'ocularSurfaceStainingKeys';
  testLabels: string[];
  multiTest: boolean;
  leastInvasive?: string;
}

@Component({
  selector: 'csi-dews-iii-homeostasis-markers',
  templateUrl: 'dews-iii-homeostasis-markers.component.html',
  styleUrls: ['dews-iii-homeostasis-markers.component.scss']
})
export class DewsIIIHomeostasisMarkersComponent implements OnInit, OnDestroy {
  @Input() formGroup: DryEyeFormGroup;
  @Input() assessment: any;
  @Input() onQuestionnaireLinked$: Observable<any>;

  tearFilmStabilityKeys: string[] = HOMEOSTASIS_SIGN_KEYS.tearFilmStability;
  osmolarityKeys: string[] = HOMEOSTASIS_SIGN_KEYS.osmolarity;
  ocularSurfaceStainingKeys: string[] = HOMEOSTASIS_SIGN_KEYS.ocularSurfaceStaining;

  categoryKeys: HomeostasisCategoryKey[] = ['tearFilmStability', 'osmolarity', 'ocularSurfaceStaining'];
  selectedCategory: HomeostasisCategoryKey = 'tearFilmStability';

  readonly categoryMeta: { [K in HomeostasisCategoryKey]: HomeostasisCategoryMeta } = {
    tearFilmStability: {
      code: '3A',
      title: 'Tear Film Stability',
      thresholds: 'NIBUT < 10 seconds | FBUT < 5 seconds',
      testKeysRef: 'tearFilmStabilityKeys',
      testLabels: ['NIBUT', 'ITBUT (FBUT)'],
      multiTest: true,
      leastInvasive: 'NIBUT is the least invasive option and is recommended when available.'
    },
    osmolarity: {
      code: '3B',
      title: 'Tear Osmolarity',
      thresholds: '≥ 308 mOsm/L in either eye | Interocular difference > 8 mOsm/L',
      testKeysRef: 'osmolarityKeys',
      testLabels: ['Osmolarity'],
      multiTest: false
    },
    ocularSurfaceStaining: {
      code: '3C',
      title: 'Ocular Surface Staining',
      thresholds: 'Corneal punctate spots > 5 | Conjunctival punctate spots > 9 | Lid margin staining ≥ 2mm & ≥ 25% width',
      testKeysRef: 'ocularSurfaceStainingKeys',
      testLabels: ['Fluorescein staining', 'Corneal staining pattern', 'Corneal staining extent', 'LWE'],
      multiTest: true,
      leastInvasive: 'Fluorescein staining is the least invasive option and is typically performed first.'
    }
  };

  categoryResults: { [key: string]: HomeostasisResult } = {};

  categoryStatusLabels: { [key: string]: string } = {};
  categoryStatusClasses: { [key: string]: string } = {};
  categoryStatusChipColours: { [key: string]: ChipColour } = {};
  categoriesTestedCount = 0;
  hasPositiveMarkerFlag = false;
  insufficientToRuleOut = false;

  constructor(
    public schemaService: SchemaService,
    private dewsIIIService: DewsIIIService
  ) {}

  ngOnInit() {
    this.initCategoryResults();
    this.initCategoryTracking();

    // Re-evaluate when questionnaires are linked (controls get new values)
    if (this.onQuestionnaireLinked$) {
      this.onQuestionnaireLinked$
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.evaluateAllCategories();
        });
    }
  }

  ngOnDestroy() {}

  selectCategory(key: HomeostasisCategoryKey): void {
    this.selectedCategory = key;
  }

  get activeTestKeys(): string[] {
    return this.getCategoryTestKeys(this.selectedCategory);
  }

  getCategoryTestKeys(key: HomeostasisCategoryKey): string[] {
    const ref = this.categoryMeta[key].testKeysRef;
    if (ref === 'tearFilmStabilityKeys') { return this.tearFilmStabilityKeys; }
    if (ref === 'osmolarityKeys') { return this.osmolarityKeys; }
    return this.ocularSurfaceStainingKeys;
  }

  private initCategoryResults() {
    const categories: HomeostasisCategory[] = ['tearFilmStability', 'osmolarity', 'ocularSurfaceStaining'];
    categories.forEach(category => {
      this.categoryResults[category] = {
        category,
        performed: false,
        positive: false
      };
    });

    if (this.formGroup.controls['dewsHomeostasisResults']) {
      const saved = this.formGroup.controls['dewsHomeostasisResults'].value;
      if (saved && Array.isArray(saved)) {
        saved.forEach(result => {
          if (this.categoryResults[result.category]) {
            this.categoryResults[result.category] = result;
          }
        });
      }
    } else {
      this.formGroup.addControl(
        'dewsHomeostasisResults',
        new FormControl({ value: null, disabled: this.formGroup.disabled })
      );
    }
  }

  private initCategoryTracking() {
    setTimeout(() => {
      this.evaluateAllCategories();

      const allSignKeys = [].concat(
        HOMEOSTASIS_SIGN_KEYS.tearFilmStability,
        HOMEOSTASIS_SIGN_KEYS.osmolarity,
        HOMEOSTASIS_SIGN_KEYS.ocularSurfaceStaining
      );

      allSignKeys.forEach(signKey => {
        const rightKey = 'RIGHT_' + signKey;
        const leftKey = 'LEFT_' + signKey;

        [rightKey, leftKey].forEach(controlKey => {
          if (this.formGroup.controls[controlKey]) {
            this.formGroup.controls[controlKey].valueChanges
              .pipe(untilDestroyed(this))
              .subscribe(() => {
                this.evaluateAllCategories();
              });
          }
        });
      });
    });
  }

  evaluateAllCategories() {
    this.evaluateTearFilmStability();
    this.evaluateOsmolarity();
    this.evaluateOcularSurfaceStaining();
    this.saveResults();
    this.updateDisplayProperties();
  }

  private updateDisplayProperties() {
    const categories = ['tearFilmStability', 'osmolarity', 'ocularSurfaceStaining'];
    categories.forEach(category => {
      this.categoryStatusLabels[category] = this.computeCategoryStatusLabel(category);
      this.categoryStatusClasses[category] = this.computeCategoryStatusClass(category);
      this.categoryStatusChipColours[category] = this.computeCategoryChipColour(category);
    });

    const results = this.getResultsArray();
    this.categoriesTestedCount = this.dewsIIIService.getTestCategoriesPerformed(results);
    this.hasPositiveMarkerFlag = this.dewsIIIService.hasPositiveHomeostasisMarker(results);
    this.insufficientToRuleOut = this.dewsIIIService.isInsufficientToRuleOut(results);
  }

  private evaluateTearFilmStability() {
    const controls = this.formGroup.controls;
    let hasData = false;
    let isPositive = false;

    const rightNibut = controls['RIGHT_NITBUT'] && controls['RIGHT_NITBUT'].value;
    const leftNibut = controls['LEFT_NITBUT'] && controls['LEFT_NITBUT'].value;
    const rightItbut = controls['RIGHT_ITBUT'] && controls['RIGHT_ITBUT'].value;
    const leftItbut = controls['LEFT_ITBUT'] && controls['LEFT_ITBUT'].value;

    if (rightNibut !== null && rightNibut !== undefined) {
      hasData = true;
      if (this.dewsIIIService.evaluateNibut(rightNibut)) { isPositive = true; }
    }
    if (leftNibut !== null && leftNibut !== undefined) {
      hasData = true;
      if (this.dewsIIIService.evaluateNibut(leftNibut)) { isPositive = true; }
    }
    if (rightItbut !== null && rightItbut !== undefined) {
      hasData = true;
      if (this.dewsIIIService.evaluateFbut(rightItbut)) { isPositive = true; }
    }
    if (leftItbut !== null && leftItbut !== undefined) {
      hasData = true;
      if (this.dewsIIIService.evaluateFbut(leftItbut)) { isPositive = true; }
    }

    this.categoryResults['tearFilmStability'] = {
      category: 'tearFilmStability',
      performed: hasData,
      positive: isPositive
    };
  }

  private evaluateOsmolarity() {
    const controls = this.formGroup.controls;
    const rightVal = controls['RIGHT_OSMOLARITY'] && controls['RIGHT_OSMOLARITY'].value;
    const leftVal = controls['LEFT_OSMOLARITY'] && controls['LEFT_OSMOLARITY'].value;
    const hasData = (rightVal !== null && rightVal !== undefined) ||
      (leftVal !== null && leftVal !== undefined);

    let higherEye = rightVal;
    let lowerEye = leftVal;
    if (rightVal !== null && rightVal !== undefined && leftVal !== null && leftVal !== undefined) {
      higherEye = Math.max(rightVal, leftVal);
      lowerEye = Math.min(rightVal, leftVal);
    }

    const isPositive = hasData && this.dewsIIIService.evaluateOsmolarity(
      higherEye !== null && higherEye !== undefined ? higherEye : 0,
      lowerEye !== null && lowerEye !== undefined ? lowerEye : 0
    );

    this.categoryResults['osmolarity'] = {
      category: 'osmolarity',
      performed: hasData,
      positive: isPositive
    };
  }

  private evaluateOcularSurfaceStaining() {
    const controls = this.formGroup.controls;
    let hasData = false;
    let isPositive = false;

    const stainingKeys = ['FLUORESCEIN_STAINING', 'CORNEAL_STAINING_PATTERN', 'CORNEAL_STAINING_EXTENT', 'LWE'];

    stainingKeys.forEach(function(key) {
      const rightVal = controls['RIGHT_' + key] && controls['RIGHT_' + key].value;
      const leftVal = controls['LEFT_' + key] && controls['LEFT_' + key].value;

      if (rightVal !== null && rightVal !== undefined) { hasData = true; }
      if (leftVal !== null && leftVal !== undefined) { hasData = true; }
    });

    // Fluorescein staining: any grade above grade0/noStaining is positive
    const rightStaining = controls['RIGHT_FLUORESCEIN_STAINING'] && controls['RIGHT_FLUORESCEIN_STAINING'].value;
    const leftStaining = controls['LEFT_FLUORESCEIN_STAINING'] && controls['LEFT_FLUORESCEIN_STAINING'].value;

    if (rightStaining && rightStaining !== 'grade0' && rightStaining !== 'noStaining') {
      isPositive = true;
    }
    if (leftStaining && leftStaining !== 'grade0' && leftStaining !== 'noStaining') {
      isPositive = true;
    }

    // Corneal staining extent (Vital Dye): grade-based or quantitative
    ['RIGHT_CORNEAL_STAINING_EXTENT', 'LEFT_CORNEAL_STAINING_EXTENT'].forEach(function(controlKey) {
      const val = controls[controlKey] && controls[controlKey].value;
      if (val !== null && val !== undefined) {
        if (val === 'severe' || val === 'moderate' || val === 'mild') {
          isPositive = true;
        } else if (val !== 'grade0' && val !== 'normal') {
          isPositive = true;
        }
      }
    });

    // LWE (Lid Wiper Epitheliopathy): any grade above noLidWiper/grade0 is positive
    ['RIGHT_LWE', 'LEFT_LWE'].forEach(function(controlKey) {
      const val = controls[controlKey] && controls[controlKey].value;
      if (val !== null && val !== undefined && val !== 'noLidWiper' && val !== 'grade0') {
        isPositive = true;
      }
    });

    this.categoryResults['ocularSurfaceStaining'] = {
      category: 'ocularSurfaceStaining',
      performed: hasData,
      positive: isPositive
    };
  }

  private saveResults() {
    const results = this.getResultsArray();
    if (this.formGroup.controls['dewsHomeostasisResults']) {
      this.formGroup.controls['dewsHomeostasisResults'].setValue(results);
    }
  }

  private computeCategoryStatusLabel(category: string): string {
    const result = this.categoryResults[category];
    if (!result || !result.performed) {
      return 'Not Performed';
    }
    return result.positive ? 'Positive' : 'Negative';
  }

  private computeCategoryStatusClass(category: string): string {
    const result = this.categoryResults[category];
    if (!result || !result.performed) {
      return 'status-not-performed';
    }
    return result.positive ? 'status-positive' : 'status-negative';
  }

  private computeCategoryChipColour(category: string): ChipColour {
    const result = this.categoryResults[category];
    if (!result || !result.performed) {
      return 'grey-no-outline';
    }
    return result.positive ? 'red-no-outline' : 'green-no-outline';
  }

  private getResultsArray(): HomeostasisResult[] {
    return [
      this.categoryResults['tearFilmStability'],
      this.categoryResults['osmolarity'],
      this.categoryResults['ocularSurfaceStaining']
    ];
  }
}
